import React from 'react';
import { Star, Shield, TrendingUp, Award, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

interface CreditScoreWidgetProps {
  data: any;
}

/**
 * Configuration des niveaux de cote crédit
 * Basé sur les standards de notation (AAA à D)
 */
const RATING_CONFIG: Record<string, {
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
  description: string;
  minScore: number;
  maxScore: number;
}> = {
  'AAA': { color: 'text-emerald-600', bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', borderColor: 'border-emerald-200 dark:border-emerald-800', label: 'Excellent', description: 'Risque minimal', minScore: 90, maxScore: 100 },
  'AA+': { color: 'text-emerald-500', bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', borderColor: 'border-emerald-200 dark:border-emerald-800', label: 'Très bon', description: 'Risque très faible', minScore: 85, maxScore: 89 },
  'AA': { color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-900/20', borderColor: 'border-green-200 dark:border-green-800', label: 'Très bon', description: 'Risque très faible', minScore: 80, maxScore: 84 },
  'AA-': { color: 'text-green-500', bgColor: 'bg-green-50 dark:bg-green-900/20', borderColor: 'border-green-200 dark:border-green-800', label: 'Bon', description: 'Risque faible', minScore: 75, maxScore: 79 },
  'A+': { color: 'text-lime-600', bgColor: 'bg-lime-50 dark:bg-lime-900/20', borderColor: 'border-lime-200 dark:border-lime-800', label: 'Bon', description: 'Risque faible', minScore: 70, maxScore: 74 },
  'A': { color: 'text-lime-500', bgColor: 'bg-lime-50 dark:bg-lime-900/20', borderColor: 'border-lime-200 dark:border-lime-800', label: 'Satisfaisant', description: 'Risque modéré-faible', minScore: 65, maxScore: 69 },
  'A-': { color: 'text-yellow-600', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', borderColor: 'border-yellow-200 dark:border-yellow-800', label: 'Satisfaisant', description: 'Risque modéré', minScore: 60, maxScore: 64 },
  'BBB+': { color: 'text-yellow-500', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', borderColor: 'border-yellow-200 dark:border-yellow-800', label: 'Moyen', description: 'Risque modéré', minScore: 55, maxScore: 59 },
  'BBB': { color: 'text-amber-500', bgColor: 'bg-amber-50 dark:bg-amber-900/20', borderColor: 'border-amber-200 dark:border-amber-800', label: 'Moyen', description: 'Risque acceptable', minScore: 50, maxScore: 54 },
  'BBB-': { color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-900/20', borderColor: 'border-amber-200 dark:border-amber-800', label: 'Passable', description: 'Risque élevé', minScore: 45, maxScore: 49 },
  'BB+': { color: 'text-orange-500', bgColor: 'bg-orange-50 dark:bg-orange-900/20', borderColor: 'border-orange-200 dark:border-orange-800', label: 'Spéculatif', description: 'Risque significatif', minScore: 40, maxScore: 44 },
  'BB': { color: 'text-orange-600', bgColor: 'bg-orange-50 dark:bg-orange-900/20', borderColor: 'border-orange-200 dark:border-orange-800', label: 'Spéculatif', description: 'Risque élevé', minScore: 35, maxScore: 39 },
  'BB-': { color: 'text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20', borderColor: 'border-red-200 dark:border-red-800', label: 'À risque', description: 'Risque très élevé', minScore: 30, maxScore: 34 },
  'B+': { color: 'text-red-500', bgColor: 'bg-red-50 dark:bg-red-900/20', borderColor: 'border-red-200 dark:border-red-800', label: 'À risque', description: 'Vulnérable', minScore: 25, maxScore: 29 },
  'B': { color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-900/20', borderColor: 'border-red-200 dark:border-red-800', label: 'Critique', description: 'Très vulnérable', minScore: 20, maxScore: 24 },
  'B-': { color: 'text-red-700', bgColor: 'bg-red-50 dark:bg-red-900/20', borderColor: 'border-red-200 dark:border-red-800', label: 'Critique', description: 'Défaut probable', minScore: 15, maxScore: 19 },
  'CCC': { color: 'text-red-800', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-300 dark:border-red-700', label: 'Défaillance', description: 'Défaut imminent', minScore: 10, maxScore: 14 },
  'CC': { color: 'text-red-900', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-300 dark:border-red-700', label: 'Défaillance', description: 'Défaut', minScore: 5, maxScore: 9 },
  'C': { color: 'text-red-950', bgColor: 'bg-red-200 dark:bg-red-900/40', borderColor: 'border-red-400 dark:border-red-600', label: 'Défaut', description: 'En faillite', minScore: 1, maxScore: 4 },
  'D': { color: 'text-gray-900 dark:text-gray-100', bgColor: 'bg-gray-200 dark:bg-gray-800', borderColor: 'border-gray-400 dark:border-gray-600', label: 'Défaut', description: 'Non évaluable', minScore: 0, maxScore: 0 },
  'N/A': { color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-800', borderColor: 'border-gray-200 dark:border-gray-700', label: 'Non disponible', description: 'En attente d\'évaluation', minScore: 0, maxScore: 0 },
};

/**
 * Obtenir la configuration pour une note financière
 */
const getRatingConfig = (rating: string) => {
  const config = RATING_CONFIG[rating] || RATING_CONFIG['N/A'];
  return config;
};

/**
 * Obtenir la couleur du score (gradient)
 */
const getScoreColor = (score: number): string => {
  if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 60) return 'text-green-600 dark:text-green-400';
  if (score >= 40) return 'text-yellow-600 dark:text-yellow-400';
  if (score >= 20) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
};

/**
 * Obtenir l'icône en fonction du score
 */
const getScoreIcon = (score: number) => {
  if (score >= 80) return <Shield className="h-5 w-5 text-emerald-500" />;
  if (score >= 60) return <CheckCircle2 className="h-5 w-5 text-green-500" />;
  if (score >= 40) return <Info className="h-5 w-5 text-yellow-500" />;
  if (score >= 20) return <AlertTriangle className="h-5 w-5 text-orange-500" />;
  return <AlertTriangle className="h-5 w-5 text-red-500" />;
};

/**
 * Widget de Cote de Crédit Wanzo
 * Affiche le score de crédit ML et la notation financière
 */
export function CreditScoreWidget({ data }: CreditScoreWidgetProps) {
  // Extraire les données de différentes sources possibles
  const creditScore = 
    data?.keyPerformanceIndicators?.creditScore ?? 
    data?.creditScore ?? 
    data?.kpi?.creditScore ?? 
    0;
    
  const financialRating = 
    data?.keyPerformanceIndicators?.financialRating ?? 
    data?.financialRating ?? 
    data?.kpi?.financialRating ?? 
    'N/A';
  
  const ratingConfig = getRatingConfig(financialRating);
  const scoreColor = getScoreColor(creditScore);
  
  // Calculer le pourcentage pour la barre de progression circulaire
  const progressPercent = Math.min(100, Math.max(0, creditScore));
  const circumference = 2 * Math.PI * 36; // rayon = 36
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;
  
  // Si pas de données
  if (creditScore === 0 && financialRating === 'N/A') {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[180px] text-center p-4">
        <div className="p-3 rounded-full bg-muted mb-3">
          <Star className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          Cote de crédit
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          En cours d'évaluation...
        </p>
        <p className="text-xs text-muted-foreground/70 mt-2 max-w-[200px]">
          Activez le partage de données pour obtenir votre cote de crédit ML
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-[180px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-primary" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Cote de Crédit Wanzo
          </span>
        </div>
        {getScoreIcon(creditScore)}
      </div>
      
      {/* Score circulaire */}
      <div className="flex-1 flex items-center justify-center gap-6">
        {/* Cercle de progression */}
        <div className="relative">
          <svg className="w-24 h-24 transform -rotate-90">
            {/* Cercle de fond */}
            <circle
              cx="48"
              cy="48"
              r="36"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-muted/30"
            />
            {/* Cercle de progression */}
            <circle
              cx="48"
              cy="48"
              r="36"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              className={scoreColor}
              style={{
                strokeDasharray: circumference,
                strokeDashoffset: strokeDashoffset,
                transition: 'stroke-dashoffset 0.5s ease-in-out'
              }}
            />
          </svg>
          {/* Score au centre */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-2xl font-bold ${scoreColor}`}>
              {creditScore}
            </span>
            <span className="text-[10px] text-muted-foreground">/100</span>
          </div>
        </div>
        
        {/* Notation et description */}
        <div className="flex flex-col gap-2">
          {/* Badge notation */}
          <div className={`
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border
            ${ratingConfig.bgColor} ${ratingConfig.borderColor}
          `}>
            <Star className={`h-4 w-4 ${ratingConfig.color}`} />
            <span className={`text-lg font-bold ${ratingConfig.color}`}>
              {financialRating}
            </span>
          </div>
          
          {/* Label et description */}
          <div>
            <p className={`text-sm font-semibold ${ratingConfig.color}`}>
              {ratingConfig.label}
            </p>
            <p className="text-xs text-muted-foreground">
              {ratingConfig.description}
            </p>
          </div>
        </div>
      </div>
      
      {/* Footer - Barre de progression linéaire */}
      <div className="mt-4 pt-3 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>Score ML</span>
          <span className={scoreColor}>{creditScore} pts</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              creditScore >= 80 ? 'bg-emerald-500' :
              creditScore >= 60 ? 'bg-green-500' :
              creditScore >= 40 ? 'bg-yellow-500' :
              creditScore >= 20 ? 'bg-orange-500' :
              'bg-red-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </div>
      </div>
    </div>
  );
}
